import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Search, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { posts, formatPostDate } from '@/lib/blog';

const PER_PAGE = 9;

// Filter chips: tags used on 2+ posts, most-used first. Single-use tags are
// omitted from the bar to keep it clean — they're still matched by search.
const allTags = (() => {
  const counts = new Map<string, number>();
  posts.forEach((p) => (p.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
  return [...counts.entries()]
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tag, count]) => ({ tag, count }));
})();

const Blog = () => {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Any filter change resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [query, activeTag]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (activeTag && !(p.tags || []).includes(activeTag)) return false;
      if (!q) return true;
      const hay = `${p.title} ${p.description} ${(p.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, activeTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  const goToPage = (n: number) => {
    setPage(n);
    if (typeof document !== 'undefined') {
      document.getElementById('post-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // JSON-LD lists every post regardless of filter/page so crawlers see them all.
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'EZ Cloud Blog',
    description:
      'Practical writing on Oracle AP automation — Fusion and EBS integration, exception prevention, and finance operations.',
    url: 'https://ezcloud.ai/blog',
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      url: `https://ezcloud.ai/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Blog — Oracle AP Automation Insights | EZ Cloud"
        description="Practical, Oracle-first writing on AP automation: Fusion and EBS integration, WebCenter migration, AI email exception prevention, fund accounting, and finance operations."
        canonical="https://ezcloud.ai/blog"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Blog', url: 'https://ezcloud.ai/blog' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-5xl">
        <header className="mb-10 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Blog</h1>
          <p className="text-lg text-gray-600">
            Practical, Oracle-first writing on AP automation — Fusion and EBS integration, exception
            prevention, and how finance teams actually run accounts payable.
          </p>
        </header>

        {/* Search */}
        <div className="relative mb-5 max-w-xl">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search posts — e.g. 'fund accounting', 'WebCenter', 'duplicate'"
            aria-label="Search blog posts"
            className="w-full rounded-lg border border-gray-300 bg-white pl-11 pr-10 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Tag filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTag(null)}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${
              activeTag === null
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All topics
          </button>
          {allTags.map(({ tag, count }) => (
            <button
              key={tag}
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${
                activeTag === tag
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tag} <span className="opacity-60">{count}</span>
            </button>
          ))}
        </div>

        {/* Result count */}
        <p className="text-sm text-gray-500 mb-6" aria-live="polite">
          {filtered.length === posts.length
            ? `${posts.length} posts`
            : `${filtered.length} of ${posts.length} posts`}
          {activeTag && ` · ${activeTag}`}
          {query && ` · "${query}"`}
        </p>

        {/* Post list */}
        <div id="post-list" className="space-y-6 scroll-mt-24">
          {pageItems.length === 0 ? (
            <Card className="p-10 text-center bg-white border border-gray-200">
              <p className="text-gray-600">
                No posts match{query && ` "${query}"`}
                {activeTag && ` in ${activeTag}`}. Try a different search or topic.
              </p>
              <button
                onClick={() => {
                  setQuery('');
                  setActiveTag(null);
                }}
                className="mt-3 text-sm font-semibold text-primary hover:text-accent"
              >
                Clear filters
              </button>
            </Card>
          ) : (
            pageItems.map((p) => (
              <Card
                key={p.slug}
                className="p-6 md:p-8 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <Link to={`/blog/${p.slug}`} className="group block">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(p.tags || []).slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-3 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    {p.title}
                  </h2>
                  <p className="text-gray-600 mb-4">{p.description}</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" aria-hidden />
                      {formatPostDate(p.date)}
                    </span>
                    {p.readingTime && (
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" aria-hidden />
                        {p.readingTime}
                      </span>
                    )}
                    <span className="inline-flex items-center font-semibold text-primary group-hover:text-accent transition-colors ml-auto">
                      Read
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" aria-hidden />
                    </span>
                  </div>
                </Link>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="flex items-center justify-center gap-1.5 mt-12" aria-label="Pagination">
            <button
              onClick={() => goToPage(current - 1)}
              disabled={current === 1}
              aria-label="Previous page"
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => goToPage(n)}
                aria-label={`Page ${n}`}
                aria-current={n === current ? 'page' : undefined}
                className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                  n === current
                    ? 'bg-primary text-white'
                    : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => goToPage(current + 1)}
              disabled={current === totalPages}
              aria-label="Next page"
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </nav>
        )}
      </main>
    </div>
  );
};

export default Blog;
